import theme from "@shared/utils/themeColors";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { FaCalculator } from "react-icons/fa";
import { useDebouncedCallback } from "use-debounce";
import { DEBOUNCE_DELAY } from "../../../features/tests/utils/constants";
import { MathSymbolKeyboard } from "./MathSymbolKeyboard";

interface DebouncedTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  showMathKeyboard?: boolean;
}

/**
 * A reusable textarea component that debounces changes to avoid excessive re-renders
 */
export const DebouncedTextarea: React.FC<DebouncedTextareaProps> = ({
  value: initialValue,
  onChange,
  placeholder,
  className = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white",
  rows = 7,
  disabled = false,
  required = false,
  autoFocus = false,
  showMathKeyboard = false,
}) => {
  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState<string>(initialValue || "");

  // State to control the visibility of the math keyboard
  const [showKeyboard, setShowKeyboard] = useState<boolean>(false);

  // Reference to track if the update is coming from local change or prop change
  const isLocalChange = useRef(false);

  // Reference to the textarea element
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update local value when prop changes, but only if it's not a local change
  useEffect(() => {
    if (!isLocalChange.current) {
      setLocalValue(initialValue || "");
    }
    // Reset the flag after each render
    isLocalChange.current = false;
  }, [initialValue]);

  // Debounced callback for onChange
  const debouncedOnChange = useDebouncedCallback((value: string) => {
    // This will trigger a prop change, but we want to ignore it
    // since we already updated the local state
    isLocalChange.current = true;
    onChange(value);
  }, DEBOUNCE_DELAY);

  // Handle textarea change
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  // Handle inserting a math symbol at the current cursor position
  const insertSymbol = (symbol: string) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Create the new value with the symbol inserted at the cursor position
      const newValue =
        localValue.substring(0, start) + symbol + localValue.substring(end);

      // Update the local value
      setLocalValue(newValue);
      debouncedOnChange(newValue);

      // Set the cursor position after the inserted symbol
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + symbol.length,
          start + symbol.length
        );
      }, 0);
    }
  };

  // Toggle the math keyboard visibility
  const toggleKeyboard = () => {
    setShowKeyboard(!showKeyboard);
  };

  return (
    <div className="relative">
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder}
          className={className}
          rows={rows}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
        />
        {showMathKeyboard && (
          <button
            type="button"
            onClick={toggleKeyboard}
            className={`absolute right-2 bottom-2 p-1 ${theme.text.muted}`}
            title="Mathematical symbols"
          >
            <FaCalculator size={16} />
          </button>
        )}
      </div>

      {showKeyboard && (
        <MathSymbolKeyboard
          onSelectSymbol={insertSymbol}
          onClose={() => setShowKeyboard(false)}
        />
      )}
    </div>
  );
};
